import React, { useEffect, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import AdminLayout from "admin/AdminLayout";
import { getSettings, updateSettings, DEFAULT_SETTINGS } from "lib/settings";

const FIELDS = [
  { name: "address", label: "Factory / Business Address", type: "textarea" },
  { name: "email", label: "Contact Email", type: "email" },
  { name: "footerPhone", label: "Phone (shown in footer)", type: "text" },
  { name: "contactPhone", label: "Phone(s) (shown on Contact page)", type: "text" },
  { name: "gstin", label: "GSTIN / UIN", type: "text" },
  { name: "pan", label: "PAN / IT No.", type: "text" },
  { name: "state", label: "State", type: "text" },
  { name: "placeOfSupply", label: "Place of Supply", type: "text" },
  { name: "licenseNo", label: "FSSAI License No.", type: "text" },
  { name: "instagramUrl", label: "Instagram URL", type: "url" },
  { name: "linkedinUrl", label: "LinkedIn URL", type: "url" },
  { name: "mapEmbedUrl", label: "Google Maps Embed URL", type: "url" },
];

export default function AdminSettings() {
  const [form, setForm] = useState(DEFAULT_SETTINGS);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [formError, setFormError] = useState("");

  useEffect(() => {
    getSettings()
      .then(setForm)
      .finally(() => setIsLoading(false));
  }, []);

  const handleChange = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setFormError("");
    try {
      const saved = await updateSettings(form);
      setForm(saved);
      toast.success("Contact details updated.");
    } catch (err) {
      setFormError(err.message || "Could not save changes.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <AdminLayout>
      <ToastContainer position="bottom-right" />

      <div className="mb-6">
        <h1 className="font-display text-2xl font-bold text-slate-800 sm:text-3xl">Contact Details</h1>
        <p className="mt-1 text-sm text-slate-500">
          Shown in the site footer and on the Contact Us page.
        </p>
      </div>

      {isLoading ? (
        <div className="h-64 animate-pulse rounded-xl bg-slate-100" />
      ) : (
        <form
          onSubmit={handleSubmit}
          className="max-w-2xl space-y-5 rounded-xl border border-slate-200 bg-white p-6"
        >
          {FIELDS.map((field) => (
            <label key={field.name} className="block" htmlFor={field.name}>
              <span className="mb-1.5 block text-sm font-medium text-slate-700">{field.label}</span>
              {field.type === "textarea" ? (
                <textarea
                  id={field.name}
                  value={form[field.name] || ""}
                  onChange={(e) => handleChange(field.name, e.target.value)}
                  rows={3}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 focus:border-maroon-400 focus:outline-none focus:ring-2 focus:ring-maroon-100"
                />
              ) : (
                <input
                  id={field.name}
                  type={field.type}
                  value={form[field.name] || ""}
                  onChange={(e) => handleChange(field.name, e.target.value)}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 focus:border-maroon-400 focus:outline-none focus:ring-2 focus:ring-maroon-100"
                />
              )}
            </label>
          ))}

          {formError && (
            <p role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
              {formError}
            </p>
          )}

          <div className="flex justify-end border-t border-slate-100 pt-4">
            <button
              type="submit"
              disabled={isSaving}
              className="rounded-lg bg-maroon-700 px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-maroon-800 disabled:opacity-60"
            >
              {isSaving ? "Saving…" : "Save Changes"}
            </button>
          </div>
        </form>
      )}
    </AdminLayout>
  );
}
